// Pure utilities used across the Module Builder UI. No React, no I/O.
// Three concerns:
//  1. Library shaping — flattenCatalog: dedupes questions by (kind, title) and
//     groups answer-set variants so the picker stays usable when the catalog
//     has many near-duplicate questions.
//  2. Per-question display — getQuestionDisplayMeta: resolves the effective
//     title/prompt/kind/answers given a question id and any draft overrides.
//  3. Tabular text helpers — parseDelimitedRows, parseOptionsText, etc. —
//     used by the activity paste view and the question editor's options field.

import type {
  CatalogInstrument,
  FlatLibraryQuestion,
  PreviewKind,
  QuestionDisplayMeta
} from "./catalogTypes";

// Minimal structural shape consumed by getQuestionDisplayMeta. The draft's
// QuestionOverride (in ./types.ts) is a superset of this — TypeScript's
// structural typing accepts the larger object where this smaller one is
// expected. Defined inline so this file doesn't depend on the draft layer.
type DisplayOverride = {
  title?: string;
  prompt?: string;
  options?: { label: string; value: string; helpText?: string }[];
  /** @deprecated Use `options`; kept for backward compatibility. */
  optionsText?: string;
  type?: PreviewKind;
};

export type ParsedActivityRow = {
  title: string;
  description: string;
  category: string;
};

export function inferPreviewKind(
  inputTypeName: string | null,
  optionCount: number
): PreviewKind {
  if (optionCount === 0) {
    return "feedback";
  }
  const s = (inputTypeName ?? "").toLowerCase();
  if (
    s.includes("multi") ||
    s.includes("checkbox") ||
    s.includes("select all") ||
    s.includes("multiple")
  ) {
    return "multi";
  }
  if (
    s.includes("text") ||
    s.includes("narrative") ||
    s.includes("free") ||
    s.includes("comment") ||
    s.includes("feedback")
  ) {
    return "feedback";
  }
  return "radio";
}

function questionKindsWithFixedAnswers(kind: PreviewKind): boolean {
  return kind === "radio" || kind === "multi";
}

export function flattenCatalog(instruments: CatalogInstrument[]): FlatLibraryQuestion[] {
  type CandidateQuestion = {
    questionId: string;
    title: string;
    prompt: string;
    inputTypeName: string | null;
    kind: PreviewKind;
    usageActivityCount: number;
    requiredDefault: boolean | null;
    answers: { label: string; value: string; prompt: string | null }[];
  };

  const groupedByTitleKind = new Map<string, CandidateQuestion[]>();

  function normalize(text: string | null | undefined): string {
    return (text ?? "").trim().replace(/\s+/g, " ").toLowerCase();
  }

  function optionsSignature(
    options: { label: string; value: string; prompt: string | null }[]
  ): string {
    return [...options]
      .sort((a, b) => a.label.localeCompare(b.label) || a.value.localeCompare(b.value))
      .map((option) => `${normalize(option.label)}|${normalize(option.value)}`)
      .join("||");
  }

  for (const inst of instruments) {
    for (const q of inst.questions) {
      const answers = q.answerOptions.map((row) => ({
        label: (row.anchor && row.anchor.trim()) || row.value,
        value: row.value,
        prompt: row.helpText ?? null
      }));
      const kind = inferPreviewKind(q.inputTypeName, answers.length);
      const prompt = (q.defaultPrompt ?? "").trim();
      const usageActivityCount = q.usageActivityCount ?? 0;
      const candidate: CandidateQuestion = {
        questionId: q.id,
        title: q.title,
        prompt,
        inputTypeName: q.inputTypeName,
        kind,
        usageActivityCount,
        requiredDefault: q.required,
        answers
      };

      const groupKey = `${kind}::${normalize(q.title)}`;
      const existing = groupedByTitleKind.get(groupKey) ?? [];
      existing.push(candidate);
      groupedByTitleKind.set(groupKey, existing);
    }
  }

  const out: FlatLibraryQuestion[] = [];
  groupedByTitleKind.forEach((group) => {
    const sortedByUsage = [...group].sort((a, b) => {
      if (b.usageActivityCount !== a.usageActivityCount) {
        return b.usageActivityCount - a.usageActivityCount;
      }
      return a.questionId.localeCompare(b.questionId);
    });
    const top = sortedByUsage[0]!;

    const variantsBySignature = new Map<
      string,
      {
        id: string;
        label: string;
        usageActivityCount: number;
        prompt: string;
        answers: { label: string; value: string; prompt: string | null }[];
      }
    >();
    for (const question of sortedByUsage) {
      const signature =
        question.kind === "feedback" ? "feedback" : optionsSignature(question.answers);
      const existingVariant = variantsBySignature.get(signature);
      if (!existingVariant) {
        variantsBySignature.set(signature, {
          id: signature,
          label:
            question.kind === "feedback"
              ? "Free text"
              : `${question.answers.length} option set`,
          usageActivityCount: question.usageActivityCount,
          prompt: question.prompt,
          answers: question.answers
        });
        continue;
      }
      const previousUsage = existingVariant.usageActivityCount;
      existingVariant.usageActivityCount += question.usageActivityCount;
      if (question.usageActivityCount > previousUsage) {
        existingVariant.prompt = question.prompt;
        existingVariant.answers = question.answers;
      }
    }

    const answerSetVariants = Array.from(variantsBySignature.values()).sort((a, b) => {
      if (b.usageActivityCount !== a.usageActivityCount) {
        return b.usageActivityCount - a.usageActivityCount;
      }
      return a.id.localeCompare(b.id);
    });
    answerSetVariants.forEach((variant) => {
      if (questionKindsWithFixedAnswers(top.kind)) {
        const optionsPreview = variant.answers
          .slice(0, 3)
          .map((answer) => answer.label)
          .join(" / ");
        const suffix = variant.answers.length > 3 ? " ..." : "";
        variant.label = `${optionsPreview}${suffix}`;
      } else {
        variant.label = "Free text";
      }
    });

    const totalUsage = sortedByUsage.reduce(
      (acc, question) => acc + question.usageActivityCount,
      0
    );
    const searchBlob = [
      top.title,
      top.prompt,
      top.inputTypeName ?? "",
      String(totalUsage),
      ...answerSetVariants.map((variant) =>
        variant.answers.map((answer) => answer.label).join(" ")
      )
    ]
      .join(" ")
      .toLowerCase();

    out.push({
      questionId: top.questionId,
      title: top.title,
      prompt: top.prompt,
      inputTypeName: top.inputTypeName,
      kind: top.kind,
      usageActivityCount: totalUsage,
      requiredDefault: top.requiredDefault,
      answers: answerSetVariants[0]?.answers ?? [],
      answerSetVariants,
      searchBlob
    });
  });

  return out.sort((a, b) => {
    if (b.usageActivityCount !== a.usageActivityCount) {
      return b.usageActivityCount - a.usageActivityCount;
    }
    return a.title.localeCompare(b.title);
  });
}

// Parse CSV/TSV-style text. Quoted multi-line cells are kept intact (newlines
// inside quotes don't end the row). Empty rows are dropped.
export function parseDelimitedRows(
  input: string,
  separator: "\t" | "," | "|"
): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let index = 0;
  let inQuotes = false;

  while (index < input.length) {
    const ch = input[index]!;
    const next = input[index + 1];

    if (ch === "\"") {
      if (inQuotes && next === "\"") {
        cell += "\"";
        index += 2;
        continue;
      }
      inQuotes = !inQuotes;
      index += 1;
      continue;
    }

    if (!inQuotes && ch === separator) {
      row.push(cell.trim());
      cell = "";
      index += 1;
      continue;
    }

    if (!inQuotes && (ch === "\n" || ch === "\r")) {
      if (ch === "\r" && next === "\n") {
        index += 1;
      }
      row.push(cell.trim());
      if (row.some((value) => value.length > 0)) {
        rows.push(row);
      }
      row = [];
      cell = "";
      index += 1;
      continue;
    }

    cell += ch;
    index += 1;
  }

  row.push(cell.trim());
  if (row.some((value) => value.length > 0)) {
    rows.push(row);
  }
  return rows;
}

// Parse "Label" or "Label | Value" lines into answer-option records.
export function parseOptionsText(text: string): { label: string; value: string }[] {
  const trimmed = text.trim();
  if (!trimmed) {
    return [];
  }
  return trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const pipe = line.indexOf("|");
      if (pipe >= 0) {
        return {
          label: line.slice(0, pipe).trim(),
          value: line.slice(pipe + 1).trim() || String(index + 1)
        };
      }
      return { label: line, value: String(index + 1) };
    });
}

// Inverse of parseOptionsText.
export function answersToOptionsText(answers: { label: string; value: string }[]): string {
  if (!answers.length) {
    return "";
  }
  return answers
    .map((a) => (a.value && a.value !== a.label ? `${a.label} | ${a.value}` : a.label))
    .join("\n");
}

function baseMetaForCustomId(id: string): QuestionDisplayMeta {
  return {
    id,
    title: "Custom question (blank)",
    prompt:
      "Define prompt, response type, and answer options in the editor. Draft-only until a later import phase.",
    kind: "radio",
    sourceLabel: "New — not from library",
    answers: []
  };
}

// Resolve the display metadata for a selected question (catalog id or custom),
// folding in any draft-only overrides. `variantId` lets callers pick a
// specific answer-set variant when the catalog row has more than one; when
// omitted, falls back to the top-usage variant (which is what `row.answers`
// already points at).
export function getQuestionDisplayMeta(
  id: string,
  flatByQuestionId: Map<string, FlatLibraryQuestion>,
  overrides: Record<string, DisplayOverride>,
  variantId?: string | null
): QuestionDisplayMeta {
  const isCustom = id.startsWith("custom-");
  const base = isCustom
    ? baseMetaForCustomId(id)
    : (() => {
        const row = flatByQuestionId.get(id);
        if (!row) {
          return {
            id,
            title: id,
            prompt: "",
            kind: "radio" as PreviewKind,
            sourceLabel: "",
            answers: []
          };
        }
        const pickedVariant = variantId
          ? row.answerSetVariants.find((v) => v.id === variantId)
          : undefined;
        const baseAnswers = pickedVariant ? pickedVariant.answers : row.answers;
        return {
          id: row.questionId,
          title: row.title,
          prompt: row.prompt,
          kind: row.kind,
          sourceLabel: "Catalog question",
          answers: baseAnswers.map((a) => ({ ...a }))
        };
      })();

  const ov = overrides[id];
  const out: QuestionDisplayMeta = { ...base };
  if (!ov) {
    return out;
  }

  if (ov.title !== undefined && ov.title.trim()) {
    out.title = ov.title.trim();
  }
  if (ov.prompt !== undefined) {
    out.prompt = ov.prompt;
  }

  let effectiveKind: PreviewKind = base.kind;
  if (isCustom && ov.type) {
    effectiveKind = ov.type;
  }
  out.kind = effectiveKind;

  if (effectiveKind === "feedback") {
    out.answers = [];
  } else if (ov.options !== undefined) {
    out.answers = ov.options.map((o) => ({
      label: o.label,
      value: o.value,
      prompt: o.helpText && o.helpText.trim() ? o.helpText : null
    }));
  } else if (ov.optionsText !== undefined) {
    out.answers = parseOptionsText(ov.optionsText);
  }

  const hasDraft =
    (ov.title !== undefined && ov.title.trim()) ||
    ov.prompt !== undefined ||
    ov.options !== undefined ||
    ov.optionsText !== undefined ||
    (isCustom && ov.type);
  if (hasDraft) {
    const bsl = base.sourceLabel || "";
    out.sourceLabel = bsl + (bsl ? " · " : "") + "edited in this draft";
  }
  return out;
}
